/**
 * Fail-closed allowlist for Sentry Session Replay.
 * Empty / unset tenant or user lists => nobody is recorded.
 * Both sides must match (AND). No wildcard.
 */

const ALLOWLIST_SEPARATOR = ","

const TENANT_ENV = "REACT_APP_SENTRY_REPLAY_TENANT_SLUGS"
const USER_ENV = "REACT_APP_SENTRY_REPLAY_USER_IDS"

export function parseSentryReplayTenantAllowlist(
    env: Record<string, string | undefined> = process.env
): string[] {
    const raw = env[TENANT_ENV]
    if (typeof raw !== "string") {
        return []
    }
    return raw
        .split(ALLOWLIST_SEPARATOR)
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0)
}

export function parseSentryReplayUserAllowlist(
    env: Record<string, string | undefined> = process.env
): number[] {
    const raw = env[USER_ENV]
    if (typeof raw !== "string") {
        return []
    }
    const ids: number[] = []
    for (const entry of raw.split(ALLOWLIST_SEPARATOR)) {
        const id = parseStrictPositiveInt(entry)
        if (id !== null) {
            ids.push(id)
        }
    }
    return ids
}

/**
 * Strict positive-int parse. Rejects partial tokens (`"42abc"`), floats (`"99.5"`),
 * and non-integers — same rule for env tokens and runtime userId (fail closed).
 */
export function parseStrictPositiveInt(value: number | string | null | undefined): number | null {
    if (typeof value === "number") {
        if (!Number.isFinite(value) || !Number.isInteger(value) || value <= 0) {
            return null
        }
        return value
    }
    if (typeof value !== "string") {
        return null
    }
    const trimmed = value.trim()
    if (trimmed.length === 0) {
        return null
    }
    const id = Number.parseInt(trimmed, 10)
    if (!Number.isFinite(id) || id <= 0 || String(id) !== trimmed) {
        return null
    }
    return id
}

export function isSentryReplayAllowed(
    input: {
        tenantSlug: string | null | undefined
        userId: number | string | null | undefined
    },
    env: Record<string, string | undefined> = process.env
): boolean {
    if (typeof input.tenantSlug !== "string") {
        return false
    }
    const tenant = input.tenantSlug.trim().toLowerCase()
    if (tenant.length === 0) {
        return false
    }
    if (!parseSentryReplayTenantAllowlist(env).includes(tenant)) {
        return false
    }

    const userId = parseStrictPositiveInt(input.userId)
    if (userId === null) {
        return false
    }
    return parseSentryReplayUserAllowlist(env).includes(userId)
}
